import { Box, Button, Flex, HStack } from "@chakra-ui/react";
import { ArrowDownIcon } from "@chakra-ui/icons";
import { FormEvent, ReactNode } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import {
  BalanceMaxHeader,
  ReceiveHintHeader,
  StakeAmountField,
} from "./StakeAmountField";
import { StakeFormDetailItem, StakeFormDetails } from "./StakeFormDetails";
import { TokenPill } from "./TokenPill";
import "../../styles/MintBox.css";

export type { StakeFormDetailItem } from "./StakeFormDetails";

export type { TokenPillProps } from "./TokenPill";
export { TokenPill };

/** @deprecated Use TokenPill — kept for gradual migration */
export const TokenAddon = ({
  image,
  symbol,
}: {
  image: string;
  symbol: string;
}) => <TokenPill image={image} symbol={symbol} />;

export interface StakeFormProps {
  sectionLabel?: string;
  topContent?: ReactNode;
  inputLabel?: string;
  outputLabel?: string;
  displayedBalance?: string;
  showBalanceRow?: boolean;
  amount: string;
  receivedAmount: string;
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMax?: () => void;
  maxDisabled?: boolean;
  inputTokenSelector?: ReactNode;
  outputTokenSelector: ReactNode;
  /** @deprecated Use inputTokenSelector */
  inputAddon?: ReactNode;
  /** @deprecated Use outputTokenSelector */
  outputAddon?: ReactNode;
  receiveHint?: string;
  /** Badges or extras in the top field header (e.g. APY loss, wait time). */
  inputHeaderExtras?: ReactNode;
  /** Key-value rows below the submit button. */
  details?: StakeFormDetailItem[];
  extraFields?: ReactNode;
  /** @deprecated Use details */
  footer?: ReactNode;
  /** @deprecated Use inputHeaderExtras */
  midContent?: ReactNode;
  submitLabel: string;
  formDisabled: boolean;
  vaultPaused?: boolean;
  isSubmitting: boolean;
  onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
  amountInputType?: "text" | "number";
}

const MINT_BOX_BG = "#131B26";

export const StakeForm = ({
  topContent,
  inputLabel = "Stake",
  outputLabel = "Receive",
  displayedBalance,
  showBalanceRow = true,
  amount,
  receivedAmount,
  onAmountChange,
  onMax,
  maxDisabled,
  inputTokenSelector,
  outputTokenSelector,
  inputAddon,
  outputAddon,
  receiveHint = "No slippage",
  inputHeaderExtras,
  details,
  extraFields,
  footer,
  midContent,
  submitLabel,
  formDisabled,
  vaultPaused,
  isSubmitting,
  onSubmit,
  amountInputType = "text",
}: StakeFormProps) => {
  const { publicKey, connecting } = useWallet();
  const { setVisible } = useWalletModal();

  const inputSelector = inputTokenSelector ?? inputAddon;
  const outputSelector = outputTokenSelector ?? outputAddon;

  const headerExtras = inputHeaderExtras ?? midContent;

  const inputHeaderRight =
    headerExtras || (showBalanceRow && publicKey && displayedBalance) ? (
      <HStack
        spacing={2}
        flexWrap="wrap"
        justify="flex-end"
        align="center"
        flex={1}
        minW={0}
      >
        {headerExtras}
        {showBalanceRow && publicKey && displayedBalance && (
          <BalanceMaxHeader
            displayedBalance={displayedBalance}
            onMax={onMax}
            maxDisabled={maxDisabled}
          />
        )}
      </HStack>
    ) : null;

  return (
    <Box className="mint-box">
      {topContent && <Box mb={4}>{topContent}</Box>}

      <form onSubmit={onSubmit}>
        <StakeAmountField
          label={inputLabel}
          variant="input"
          tokenSelector={inputSelector}
          amount={amount}
          onAmountChange={onAmountChange}
          amountInputType={amountInputType}
          headerRight={inputHeaderRight}
        />

        <Flex justify="center" position="relative" zIndex={2} my={-3}>
          <Flex
            align="center"
            justify="center"
            boxSize="36px"
            borderRadius="full"
            bg={MINT_BOX_BG}
            border="3px solid"
            borderColor={MINT_BOX_BG}
            boxShadow="0 0 0 1px rgba(255,255,255,0.12)"
          >
            <ArrowDownIcon color="whiteAlpha.700" boxSize={4} />
          </Flex>
        </Flex>

        <StakeAmountField
          label={outputLabel}
          variant="output"
          tokenSelector={outputSelector}
          amount={receivedAmount}
          readOnly
          headerRight={<ReceiveHintHeader hint={receiveHint} />}
        />

        {!publicKey ? (
          <Flex justifyContent="center" width="100%" mt={4}>
            <Button
              width="100%"
              className="mint-box-button"
              isDisabled={connecting}
              onClick={() => setVisible(true)}
            >
              {connecting ? "Connecting..." : "Connect Wallet"}
            </Button>
          </Flex>
        ) : (
          <Flex justifyContent="center" width="100%" mt={4}>
            <Button
              width="100%"
              className="mint-box-button"
              isDisabled={formDisabled || vaultPaused}
              isLoading={isSubmitting}
              type="submit"
            >
              {submitLabel}
            </Button>
          </Flex>
        )}

        <StakeFormDetails items={details ?? []} />
        {!details?.length && footer}
      </form>

      {extraFields}
    </Box>
  );
};
